import React, { useEffect } from "react";
import { IconKeyboardArrowRightBlue, IconUnarchive } from "assets/svg";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { API_ENDPOINTS, FILE_BASE_URL } from "config/apiConfig";
import { ProductStatusEnum } from "common/enums/Product.enum";
import {
  fetchTransactionNotification,
  transactionCleanData,
} from "reducers/transactionSlice";
import { getDiffTime } from "common/helper/time.helper";
import { transactionNotificationText } from "common/helper/text.helper";
import { axiosInstance } from "config/axiosConfig";
import { TransactionStatusEnum } from "common/enums/TransactionStatus.enum";
import { Transaction, Product } from "common/interfaces";
import { useCommonContext } from "contexts/CommonContext";
import { productPrice } from "common/utils";

const List = () => {
  const { setHeaderTitle } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const [currentTab, setCurrentTab] = React.useState("出品中");
  const [currentLimit, setCurrentLimit] = React.useState(5);
  const [products, setProducts] = React.useState<Product[]>([]);
  const { transactionNotificationData } = useSelector(
    (state: RootState) => state.transaction,
  );

  useEffect(() => {
    dispatch(fetchTransactionNotification());
    fetchProductsByTab("出品中", 5);
    setHeaderTitle("出品内容の管理");
    return () => {
      dispatch(transactionCleanData());
    };
  }, []);

  // function
  const handleClickTab = (tab: string) => {
    if (tab === currentTab) {
      return;
    }

    setCurrentLimit(5);
    fetchProductsByTab(tab, 5);
    setCurrentTab(tab);
  };

  const handleShowMore = () => {
    const newLimit = currentLimit + 5;
    setCurrentLimit(newLimit);
    fetchProductsByTab(currentTab, newLimit);
  };

  const tagProductHtml = (product: any) => {
    if (product.status === ProductStatusEnum.DRAFT) {
      return tagProductDraftHtml;
    } else if (product.status === ProductStatusEnum.SUSPENDED) {
      return tagProductSuspendedHtml;
    }
  };

  const fetchProductInTransaction = (params: object) => {
    axiosInstance
      .get(API_ENDPOINTS.TRANSACTION_LIST, {
        params: params,
      })
      .then((res) => {
        const productList = formatTransactionIntoProducts(res.data.data);
        setProducts(productList);
      });
  };

  const formatTransactionIntoProducts = (
    transactions: Transaction[],
  ): Product[] => {
    return transactions.map((item: Transaction) => item.product);
  };

  const fetchProductsByTab = (tab: string, limit: number) => {
    const baseParams = {
      limit,
      sort: "id:desc",
    };

    switch (tab) {
      case "出品中":
        fetchProducts({
          ...baseParams,
          status: [
            ProductStatusEnum.PUBLIC,
            ProductStatusEnum.SUSPENDED,
            ProductStatusEnum.DRAFT,
          ].join(","),
        });
        return;
      case "進行中":
        fetchProductInTransaction({
          ...baseParams,
          status: [
            TransactionStatusEnum.ACCEPT,
            TransactionStatusEnum.DELIVERED_TO_SHIP,
            TransactionStatusEnum.RECEIVED,
            TransactionStatusEnum.INVOICE_ISSUED,
            TransactionStatusEnum.BUYER_REQUEST_CANCEL,
            TransactionStatusEnum.SELLER_REQUEST_CANCEL,
          ].join(","),
        });
        return;
      case "取引完了":
        fetchProductInTransaction({
          ...baseParams,
          status: [TransactionStatusEnum.COMPLETED].join(","),
        });
        return;
      case "キャンセル":
        fetchProductInTransaction({
          ...baseParams,
          status: [
            TransactionStatusEnum.BUYER_ACCEPT_CANCEL,
            TransactionStatusEnum.SELLER_ACCEPT_CANCEL,
          ].join(","),
        });
        return;
    }
  };

  const fetchProducts = (params: object) => {
    axiosInstance
      .get(API_ENDPOINTS.PRODUCT_LIST, {
        params: params,
      })
      .then((res) => {
        setProducts(res.data.data);
      });
  };

  // element
  const tagProductDraftHtml = (
    <div
      data-プロパティ1="base"
      className="min-w-14 px-2.5 py-[5px] bg-primary-light rounded-md inline-flex flex-col justify-center items-center gap-2.5"
    >
      <div className="text-center justify-start text-primary text-[11px] md:text-xs font-normal leading-tight">
        下書き
      </div>
    </div>
  );
  const tagProductSuspendedHtml = (
    <div
      data-プロパティ1="attention"
      className="min-w-14 px-2.5 py-[5px] bg-red-light rounded-md inline-flex flex-col justify-center items-center gap-2.5"
    >
      <div className="text-center justify-start text-red-dark text-[11px] md:text-xs font-normal leading-tight">
        出品停止中
      </div>
    </div>
  );

  return (
    <div className="self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-7 overflow-hidden">
      <div className="self-stretch inline-flex justify-start items-center gap-2.5">
        <div
          data-プロパティ1="base + icon"
          className="w-80 min-h-14 px-5 py-2.5 bg-primary rounded-md flex justify-center items-center gap-2.5 overflow-hidden"
        >
          <IconUnarchive className="w-[24px] h-[24px] shrink-0 aspect-[1/1]" />
          <Link
            to="/seller/product/create"
            className="text-center justify-start text-white text-[16px] md:text-lg font-bold leading-7"
          >
            新しい商品を出品する
          </Link>
        </div>
      </div>
      <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
        <div className="self-stretch flex flex-col justify-start items-start">
          <div
            data-プロパティ1="base"
            className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
          >
            <div className="w-1 self-stretch bg-primary rounded-sm" />
            <div className="flex-1 flex justify-start items-center gap-2.5">
              <div className="flex justify-start items-center gap-[5px]">
                <div className="justify-start text-primary text-[18px] md:text-xl font-bold leading-9">
                  取引についてのお知らせ
                </div>
              </div>
            </div>
          </div>

          {/*Contents*/}
          <div className="flex flex-col justify-center items-center gap-[5px] self-stretch">
            <div className="flex flex-col items-start self-stretch">
              {transactionNotificationData?.data.length === 0 ? (
                <div className="h-[80px] md:h-[110px] flex py-[10px] md:py-[15px] items-center justify-center self-stretch border-b border-neutral-border bg-white">
                  <div className="text-gray-500 font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                    表示する内容がありません
                  </div>
                </div>
              ) : (
                ""
              )}
              {/*Items*/}
              {transactionNotificationData?.data.map((transaction, index) => {
                return (
                  <Link
                    to={`/seller/product/${transaction.product.code}/transaction/${transaction.buyer_id}/${transaction.seller_id}`}
                    key={index}
                    className="h-[89px] md:h-[42px] flex py-[10px] justify-center items-center gap-[10px] self-stretch border-b border-neutral-border bg-white"
                  >
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-[7px] md:gap-[10px] flex-[1_0_0]">
                      <div className="max-h-[44px] md:max-h-[22px] md:h-[22px] flex-[1_0_0] text-black font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                        {transactionNotificationText(transaction)}
                      </div>
                      <div className="h-[18px] md:h-[19px] text-primary text-left font-['Noto Sans JP'] text-[11px] md:text-[12px] not-italic font-normal leading-[160%]">
                        {getDiffTime(transaction.commonDate)}
                      </div>
                    </div>
                    <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
        <div className="self-stretch flex flex-col justify-start items-start">
          <div className="self-stretch bg-white border-b border-neutral-border inline-flex justify-start items-start">
            <div
              onClick={() => handleClickTab("出品中")}
              className={`pt-2 ${currentTab !== "出品中" ? "pb-3" : ""} inline-flex flex-col justify-center items-center gap-2 cursor-pointer`}
            >
              <div className="px-[12px] md:px-6 flex flex-col justify-center items-center gap-2.5">
                <div
                  className={`text-center justify-start ${currentTab === "出品中" ? "text-primary" : "text-neutral-soft"} text-[14px] md:text-base whitespace-nowrap font-bold leading-relaxed`}
                >
                  出品中
                </div>
              </div>
              {currentTab === "出品中" ? (
                <div className="self-stretch h-1 bg-primary rounded-sm" />
              ) : null}
            </div>
            <div
              onClick={() => handleClickTab("進行中")}
              className={`pt-2 ${currentTab !== "進行中" ? "pb-3" : ""} inline-flex flex-col justify-center items-center gap-2 cursor-pointer`}
            >
              <div className="px-[12px] md:px-6 flex flex-col justify-center items-center gap-2.5">
                <div
                  className={`text-center justify-start ${currentTab === "進行中" ? "text-primary" : "text-neutral-soft"} text-[14px] md:text-base whitespace-nowrap font-bold leading-relaxed`}
                >
                  進行中
                </div>
              </div>
              {currentTab === "進行中" ? (
                <div className="self-stretch h-1 bg-primary rounded-sm" />
              ) : null}
            </div>
            <div
              onClick={() => handleClickTab("取引完了")}
              className={`pt-2 ${currentTab !== "取引完了" ? "pb-3" : ""} inline-flex flex-col justify-center items-center gap-2 cursor-pointer`}
            >
              <div className="px-[12px] md:px-6 flex flex-col justify-center items-center gap-2.5">
                <div
                  className={`text-center justify-start ${currentTab === "取引完了" ? "text-primary" : "text-neutral-soft"} text-[14px] md:text-base whitespace-nowrap font-bold leading-relaxed`}
                >
                  取引完了
                </div>
              </div>
              {currentTab === "取引完了" ? (
                <div className="self-stretch h-1 bg-primary rounded-sm" />
              ) : null}
            </div>
            <div
              data-プロパティ1="base"
              onClick={() => handleClickTab("キャンセル")}
              className={`pt-2 ${currentTab !== "キャンセル" ? "pb-3" : ""} inline-flex flex-col justify-center items-center gap-2 cursor-pointer`}
            >
              <div className="px-[12px] md:px-6 flex flex-col justify-center items-center gap-2.5">
                <div
                  className={`text-center justify-start ${currentTab === "キャンセル" ? "text-primary" : "text-neutral-soft"} text-[14px] md:text-base whitespace-nowrap font-bold leading-relaxed`}
                >
                  キャンセル
                </div>
              </div>
              {currentTab === "キャンセル" ? (
                <div className="self-stretch h-1 bg-primary rounded-sm" />
              ) : null}
            </div>
          </div>
          <div className="self-stretch bg-white flex flex-col justify-center items-center gap-6">
            <div className="self-stretch flex flex-col justify-center items-start">
              {products?.length === 0 ? (
                <div className="h-[80px] md:h-[110px] flex py-[10px] md:py-[15px] items-center justify-center self-stretch border-b border-neutral-border bg-white">
                  <div className="text-gray-500 font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                    現在、表示されている商品はありません。
                  </div>
                </div>
              ) : (
                products?.map((product: any) => (
                  <Link
                    to={`/seller/product/${product.code}`}
                    key={product.id}
                    data-プロパティ1="base"
                    className="self-stretch py-3.5 bg-white border-b border-neutral-border inline-flex justify-start items-center gap-5"
                  >
                    <div className="flex-1 flex justify-start items-center gap-5">
                      {product.image_url ? (
                        <img
                          src={`${FILE_BASE_URL}/${product.image_url}`}
                          alt={product.product_name}
                          className="w-[60px] h-[60px] md:w-20 md:h-20 relative bg-primary-light rounded-md"
                        />
                      ) : (
                        <div className="w-[60px] h-[60px] md:w-20 md:h-20 relative bg-primary-light rounded-md" />
                      )}
                      <div className="flex-1 inline-flex flex-col justify-center items-start gap-[5px]">
                        <div className="self-stretch justify-start text-black text-[15px] md:text-lg font-normal leading-7">
                          {product.product_name}
                        </div>
                        <div className="self-stretch justify-start text-black text-[11px] md:text-xs font-bold leading-tight">
                          {productPrice(product)}
                        </div>
                      </div>
                    </div>
                    {tagProductHtml(product)}
                    <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
                  </Link>
                ))
              )}
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-2.5">
              <div
                onClick={handleShowMore}
                className="inline-flex justify-start items-center cursor-pointer hover:opacity-80 transition-opacity"
              >
                <span className="justify-start no-underline text-blue-900 text-sm font-medium underline leading-snug">
                  全ての過去の出品履歴を見る
                </span>
                <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default List;
